import { useMemo } from "react";
import { Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateZoneZonesPostMutation } from "api";
import { SelectInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";

import { buildZoneFormSchema, ZoneFormSchema } from "./zoneFormSchema";

import { FormActions } from "../../components/FormActions";
import { PageHeader } from "../../components/PageHeader";

type CreateZoneFormProps = {
  zones: IZone[];
};

export function CreateZoneForm({ zones }: CreateZoneFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [createZone, { isLoading }] = useCreateZoneZonesPostMutation();

  const schema = useMemo(() => buildZoneFormSchema(zones, t), [zones, t]);

  const { handleSubmit, control } = useForm<ZoneFormSchema>({
    resolver: zodResolver(schema),
  });

  const onUpdateZone = async (item: ZoneFormSchema) => {
    try {
      const response = await createZone({
        zoneCreate: {
          name: item.name,
          description: item.description ?? "",
          parent_id: item.parent_id,
        },
      });

      if (response?.data?.data?.id) {
        navigate(`/administration/zones/${response.data.data.id}/settings`);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Stack
      component={"form"}
      onSubmit={handleSubmit(onUpdateZone)}
      sx={{ padding: 2, gap: 2 }}
    >
      <PageHeader title={t("create_zone")} />
      <SectionCard title={t("Zone Information")}>
        <TextInput
          label={t("Zone name")}
          control={control}
          required
          placeholder={t("zone_name_placeholder")}
          fieldName={"name"}
        />
        <TextInput
          label={t("Description (optional)")}
          control={control}
          placeholder={t("zone_description_placeholder")}
          fieldName={"description"}
        />
        <SelectInput
          label={t("Parent zone")}
          control={control}
          placeholder={t("zone_description_placeholder")}
          fieldName={"parent_id"}
          options={zones.map((zone) => ({
            ...zone,
            label: zone.name,
            disabled: !zone.hasAccess,
            padding: (zone.level - 1) * 2,
          }))}
        />
      </SectionCard>
      <FormActions
        submitText={t("Create")}
        backText={t("Cancel")}
        backHref={".."}
        isLoading={isLoading}
      />
    </Stack>
  );
}
